import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { LISTING_HEALTH_COLUMN_ID } from "./constants";

const SECTION_PARAM = "section";
const LISTING_CARD_HEADING = "Listing";
const HIGHLIGHT_CLASS = "highlight-message";
const HIGHLIGHT_MS = 2000;
const highlightTimers = new WeakMap<HTMLElement, number>();

const withSection = (path: string, heading: string) =>
  `${path}${path.includes("?") ? "&" : "?"}${new URLSearchParams({
    [SECTION_PARAM]: heading
  })}`;

export const unitSectionPath = (
  propertyId: number,
  unitId: number,
  heading: string
) => withSection(`/properties/${propertyId}/units/${unitId}`, heading);

export const propertySectionPath = (propertyId: number, heading: string) =>
  withSection(`/properties/${propertyId}`, heading);

/** The page a listing opens on, at its Listing card. */
export const listingCardPath = (pagePath: string) =>
  withSection(pagePath, LISTING_CARD_HEADING);

export const weakListingsPath = () =>
  `/listings?${new URLSearchParams({
    view: "table",
    listing_health: "weak",
    order_by: LISTING_HEALTH_COLUMN_ID,
    order_direction: "asc"
  })}`;

/** Scrolls to the card titled `heading` and lights it up; false when the page has no such card. */
export const revealSection = (heading: string) => {
  const title = Array.from(document.querySelectorAll("h3")).find(
    (h) => h.textContent?.trim() === heading
  );
  const card = title?.closest<HTMLElement>(".rounded-lg") ?? title;
  if (!card) return false;

  card.scrollIntoView({ behavior: "smooth", block: "start" });
  card.classList.remove(HIGHLIGHT_CLASS);
  // Reading layout restarts the animation when the same card is lit twice in a row.
  card.getBoundingClientRect();
  window.clearTimeout(highlightTimers.get(card));
  card.classList.add(HIGHLIGHT_CLASS);
  highlightTimers.set(
    card,
    window.setTimeout(
      () => card.classList.remove(HIGHLIGHT_CLASS),
      HIGHLIGHT_MS
    )
  );
  return true;
};

/** Opens the page at the card named by `?section=`, once the page has rendered it. */
export const useRevealSectionFromUrl = (ready: boolean) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const heading = searchParams.get(SECTION_PARAM);

  useEffect(() => {
    if (!ready || !heading) return;
    const frame = requestAnimationFrame(() => {
      revealSection(heading);
      setSearchParams(
        (current) => {
          current.delete(SECTION_PARAM);
          return current;
        },
        { replace: true }
      );
    });
    return () => cancelAnimationFrame(frame);
  }, [ready, heading]);
};
